import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isNil from 'lodash/isNil'
import isBoolean from 'lodash/isBoolean'
import isDate from 'lodash/isDate'

dayjs.extend(utc)

const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}T/
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/** Excel numFmt that keeps date cells as text (prevents serial conversion). */
export const EXCEL_TEXT_NUMFMT = '@'

/**
 * Custom validation formula for YYYY-MM-DD text in Excel.
 * Pass the cell address, e.g. "C2" — ExcelJS expects a leading "=" in formulae.
 * When allowPlaceholder is given (e.g. "-"), that exact value is also accepted
 * so the "no change" placeholder does not trip date validation.
 */
export function excelDateTextValidationFormula(
  cellAddress: string,
  allowPlaceholder?: string
): string {
  const dateCheck = `AND(LEN(${cellAddress})=10,MID(${cellAddress},5,1)="-",MID(${cellAddress},8,1)="-")`
  if (allowPlaceholder) {
    return `OR(${cellAddress}="${allowPlaceholder}",${dateCheck})`
  }
  return dateCheck
}

/**
 * Serialize a value to a display string for diff comparison.
 * ISO datetime strings (e.g. "2026-04-01T00:00:00.000Z") are truncated to
 * date-only so they match the yyyy-mm-dd strings written to and read from Excel.
 */
export function formatFieldValue(value: unknown): string {
  if (isNil(value)) return ''
  if (isBoolean(value)) return value ? 'true' : 'false'
  if (isDate(value)) return dayjs.utc(value).format('YYYY-MM-DD')
  const rawStr = String(value)
  if (ISO_DATETIME_PATTERN.test(rawStr)) return rawStr.split('T')[0]
  return rawStr
}

/**
 * Return the value to write into an Excel cell.
 * When isDateField is true, the value is written as a plain "yyyy-mm-dd" string
 * to avoid serial/timezone conversion done by ExcelJS or SheetJS.
 */
export function getExcelCellValue(
  value: unknown,
  isDateField: boolean
): string {
  if (isNil(value)) return ''
  if (isDateField) {
    const rawStr = String(value)
    if (ISO_DATETIME_PATTERN.test(rawStr)) return rawStr.split('T')[0]
    if (DATE_ONLY_PATTERN.test(rawStr)) return rawStr
    const parsedDate = isDate(value) ? value : new Date(rawStr)
    return isNaN(parsedDate.getTime())
      ? rawStr
      : dayjs.utc(parsedDate).format('YYYY-MM-DD')
  }
  if (isBoolean(value)) return value ? 'true' : 'false'
  return String(value)
}

/**
 * Validate a raw string value for a field.
 * Returns an error message when the value is invalid, or undefined when acceptable.
 * An empty string is always accepted (treated as "no change").
 */
export function validateExcelValue(
  value: string,
  opts: { isDateField?: boolean; isBoolean?: boolean; isNumber?: boolean }
): string | undefined {
  if (!value) return undefined
  if (opts.isBoolean) {
    const lower = value.toLowerCase()
    if (lower !== 'true' && lower !== 'false')
      return 'Must be "true" or "false"'
  }
  if (opts.isNumber && isNaN(parseFloat(value))) return 'Must be a number'
  if (opts.isDateField && isNaN(new Date(value).getTime()))
    return 'Invalid date'
  return undefined
}

/**
 * Convert a raw Excel string value to the correct DTO type for submission.
 */
export function parseExcelValue(
  rawValue: string,
  opts: { isBoolean?: boolean; isNumber?: boolean }
): unknown {
  if (rawValue === '') return undefined
  if (opts.isBoolean) return rawValue.toLowerCase() === 'true'
  if (opts.isNumber) {
    const n = parseFloat(rawValue)
    return isNaN(n) ? undefined : n
  }
  return rawValue
}
